/**
 * @fileoverview Custom fetch wrapper that handles 401 errors with token refresh.
 * Used by AI SDK's DefaultChatTransport for streaming chat requests.
 */

import { attemptTokenRefresh } from '@/lib/api/auth-refresh';
import { ENV } from '@/lib/config';
import { currentRequestLocale } from './interceptors/accept-language.interceptor';

/**
 * The platform cookie these callers rely on is set on the deployed parent domain, so it never
 * reaches an API served from a different origin than the dev server. Under local testing auth the
 * bearer token stands in for it, exactly as the axios clients and the other raw-fetch call sites
 * do. A caller that set its own Authorization keeps it.
 */
function withLocalAuth(init: RequestInit | undefined): RequestInit | undefined {
  if (!ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION || !ENV.LOCAL_TOKEN) return init;

  const headers = new Headers(init?.headers);
  if (headers.has('Authorization')) return init;
  headers.set('Authorization', ENV.LOCAL_TOKEN);
  return { ...init, headers };
}

/**
 * The raw-fetch counterpart of the axios Accept-Language interceptor, so a stream reads in the
 * UI language rather than the one the browser derives from the operating system.
 */
function withUiLanguage(init: RequestInit | undefined): RequestInit & { headers: Headers } {
  const headers = new Headers(init?.headers);
  if (!headers.has('Accept-Language')) headers.set('Accept-Language', currentRequestLocale());
  return { ...init, headers };
}

/**
 * Fetch wrapper that handles 401 errors by refreshing the token and retrying.
 * Uses X-Auth-Retry header to prevent infinite retry loops.
 */
export const fetchWithAuth: typeof fetch = async (input, rawInit) => {
  const init = withUiLanguage(withLocalAuth(rawInit));
  const response = await fetch(input, init);

  // Don't retry if we're already retrying or status isn't 401
  if (response.status !== 401 || init.headers.has('X-Auth-Retry')) {
    return response;
  }

  const refreshed = await attemptTokenRefresh();
  if (!refreshed) {
    return response;
  }

  // Retry with marker header to prevent infinite loops
  const retryHeaders = new Headers(init.headers);
  retryHeaders.set('X-Auth-Retry', 'true');

  return fetch(input, {
    ...init,
    headers: retryHeaders,
  });
};
